/**
 * Lets the upload dialogs outside the toolbar insert directives at the caret.
 * A composer child binds the root Lexical editor and MDXEditor's insert publisher to MarkdownStore.
 */

import { realmPlugin, addComposerChild$, insertMarkdown$, usePublisher } from '@mdxeditor/editor'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createParagraphNode, $getSelection, $isRangeSelection, LexicalEditor } from 'lexical'
import { $isHeadingNode } from '@lexical/rich-text'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { DirectiveInsert, useMarkdownStore } from '../MarkdownStore'

const directivePrefixes: Record<DirectiveInsert['type'], string> = {
   textDirective: ':',
   leafDirective: '::',
   containerDirective: ':::',
}

/** Serializes a directive to its markdown source. */
export function serializeDirective(directive: DirectiveInsert): string {
   const attributes = directive.attributes
      ? '{' + Object.entries(directive.attributes).map(([attributeName, value]) => `${attributeName}="${value}"`).join(' ') + '}'
      : ''
   return `${directivePrefixes[directive.type]}${directive.name}${attributes}`
}

/** Moves a caret sitting at the start of a non-empty heading into a new paragraph above it. */
export function $moveCaretOffHeadingStart(): void {
   const selection = $getSelection()
   if (!$isRangeSelection(selection) || !selection.isCollapsed() || selection.anchor.offset !== 0) return
   const anchorNode = selection.anchor.getNode()
   const heading = anchorNode.getTopLevelElement()
   if (!$isHeadingNode(heading) || heading.isEmpty()) return
   const isAtHeadingStart = anchorNode.is(heading) || anchorNode.is(heading.getFirstDescendant())
   if (!isAtHeadingStart) return
   // Lexical's insertNodes replaces a heading whose start holds the caret and then reuses the detached heading, which throws.
   const paragraph = $createParagraphNode()
   heading.insertBefore(paragraph)
   paragraph.select()
}

/** Inserts a directive at the caret and leaves the caret in the editor. */
function insertDirectiveAtCaret(params: { editor: LexicalEditor; insertMarkdown: (markdown: string) => void; directive: DirectiveInsert }): void {
   const markdown = serializeDirective(params.directive)
   // Inserting from the focus callback keeps the caret: MDXEditor nulls the selection when inserting into an unfocused editor.
   params.editor.focus(() => {
      params.editor.update($moveCaretOffHeadingStart)
      params.insertMarkdown(markdown)
   })
}

const DirectiveInsertionBridge = observer(function DirectiveInsertionBridge() {
   const [editor] = useLexicalComposerContext()
   const insertMarkdown = usePublisher(insertMarkdown$)
   const markdownStore = useMarkdownStore()

   useEffect(() => {
      markdownStore.setDirectiveInserter(directive => insertDirectiveAtCaret({ editor, insertMarkdown, directive }))
   }, [editor, insertMarkdown, markdownStore])

   return null
})

export const directiveInsertionPlugin = realmPlugin({
   init(realm) {
      realm.pub(addComposerChild$, DirectiveInsertionBridge)
   },
})
